import React, { useRef, useState } from "react";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import { assetUrl } from "@/lib/branding";
import { toast } from "sonner";

// Reusable branding asset uploader with live preview, replace & remove.
// shape: "wide" | "square" | "circle" | "favicon"
export function AssetUpload({ label, hint, kind = "other", value, onChange, shape = "square", testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const url = assetUrl(value);

  const pick = () => ref.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await http.post(`/uploads?kind=${encodeURIComponent(kind)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.id);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const isWide = shape === "wide";
  const box =
    shape === "circle" ? "h-16 w-16 rounded-full"
    : shape === "favicon" ? "h-12 w-12 rounded-lg"
    : isWide ? "h-24 w-full rounded-lg"
    : "h-16 w-16 rounded-lg";

  return (
    <div className="space-y-1.5" data-testid={testId}>
      {label && <label className="text-xs font-medium text-muted-foreground">{label}</label>}
      <div className={isWide ? "flex flex-col gap-2" : "flex items-center gap-3"}>
        <div className={`relative flex shrink-0 items-center justify-center overflow-hidden border border-dashed border-white/15 bg-[#0F0F0F] ${box}`}>
          {url ? (
            <img src={url} alt={label} className="h-full w-full object-contain" />
          ) : (
            <Upload className="h-4 w-4 text-muted-foreground/50" />
          )}
          {busy && <div className="absolute inset-0 flex items-center justify-center bg-black/60"><Loader2 className="h-4 w-4 animate-spin text-white" /></div>}
        </div>
        <div className={isWide ? "flex items-center justify-between gap-2" : "flex flex-col gap-1.5"}>
          <div className="flex gap-2">
            <button type="button" onClick={pick} disabled={busy} data-testid={`${testId}-upload`}
              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1 text-xs text-foreground hover:bg-white/5 disabled:opacity-50">
              <Upload className="h-3 w-3" /> {value ? "Replace" : "Upload"}
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} data-testid={`${testId}-remove`}
                className="rounded-md border border-white/10 px-2 py-1 text-xs text-muted-foreground hover:text-red-400">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {hint && <span className="text-[11px] text-muted-foreground/70">{hint}</span>}
        </div>
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onFile} />
    </div>
  );
}
